import React, { useMemo, useState } from 'react';
import { analyzeJanelasDeTempoLevantadas } from '../utils/dataAnalysis';
import { TrendingUp, TrendingDown, Users, MessageSquare, Clock, Target } from 'lucide-react';
import { formatTempo } from '../utils/excelProcessor';

/**
 * Componente para análise geral das levantadas de mão
 */
const LevantadasAnalysis = ({ metrics, showDetail, data = [] }) => {

  // formatTempo vem de utils/excelProcessor; formatDuracao removido (não utilizado)

  // Controle de faixa de tempo de resposta do atendente (min/max em minutos)
  const [minTempo, setMinTempo] = useState(0);
  const [maxTempo, setMaxTempo] = useState(5);

  const faixaTempo = useMemo(() => {
    const total = data.length || 0;
    const itens = data.filter(item => item.foiRespondida && typeof item.minutosResposta === 'number');
    const itensFaixa = itens.filter(item => item.minutosResposta >= (Number(minTempo) || 0) && item.minutosResposta <= (Number(maxTempo) || 0));
    const count = itensFaixa.length;
    const percent = total > 0 ? (count / total) * 100 : 0;
    return { count, percent };
  }, [data, minTempo, maxTempo]);

  // Melhores janelas (2h) de engajamento do lead e de resposta do atendente
  const melhoresJanelas = useMemo(() => {
    const janelas = Array.isArray(data) && data.length > 0
      ? analyzeJanelasDeTempoLevantadas(data)
      : [];
    // Amostragem mínima: considerar apenas janelas com >= 10 tickets
    const janelasValidas = janelas.filter(j => (j.totalTickets || 0) >= 10);
    if (janelasValidas.length === 0) {
      return {
        melhorEngajamento: { nome: 'N/A', taxa: 0 },
        melhorResposta: { nome: 'N/A', taxa: 0 }
      };
    }
    const melhorEng = janelasValidas.reduce((prev, cur) => (cur.taxaEngajamento > prev.taxaEngajamento ? cur : prev), janelasValidas[0]);
    const melhorResp = janelasValidas.reduce((prev, cur) => (cur.taxaResposta > prev.taxaResposta ? cur : prev), janelasValidas[0]);
    return {
      melhorEngajamento: { nome: melhorEng.nome, taxa: melhorEng.taxaEngajamento },
      melhorResposta: { nome: melhorResp.nome, taxa: melhorResp.taxaResposta }
    };
  }, [data]);

  if (!metrics) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Análise de Levantadas de Mão</h2>
        <p className="text-gray-500">Carregando dados...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <h2 className="text-xl font-semibold text-gray-800 mb-6">
        📋 Análise de Levantadas de Mão
        <span className="text-sm font-normal text-gray-500 ml-2">
          (Janelas de conversão)
        </span>
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Total de Levantadas */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Total de Levantadas</p>
              <p className="text-3xl font-bold text-blue-700">{metrics.totalLevantadasMao.toLocaleString()}</p>
              <p className="text-sm text-blue-600 mt-1">
                {metrics.totalTickets.toLocaleString()} tickets ({metrics.ticketsPorLevantada.toFixed(1)} por levantada)
              </p>
            </div>
            <Target className="h-12 w-12 text-blue-500" />
          </div>
        </div>

        {/* Melhor Janela de Engajamento do Lead (2h) */}
        <div className="bg-gradient-to-r from-teal-50 to-teal-100 rounded-lg p-6 border border-teal-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-teal-600">Melhor Janela de Engajamento (Lead)</p>
              <p className="text-2xl font-bold text-teal-700">{melhoresJanelas.melhorEngajamento.nome}</p>
              <p className="text-sm text-teal-600 mt-1">
                {melhoresJanelas.melhorEngajamento.taxa.toFixed(1)}% engajam
              </p>
            </div>
            <Users className="h-10 w-10 text-teal-500" />
          </div>
        </div>

        {/* Melhor Janela de Resposta do Atendente (2h) */}
        <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-lg p-6 border border-indigo-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-indigo-600">Melhor Janela de Resposta (Atendente)</p>
              <p className="text-2xl font-bold text-indigo-700">{melhoresJanelas.melhorResposta.nome}</p>
              <p className="text-sm text-indigo-600 mt-1">
                {melhoresJanelas.melhorResposta.taxa.toFixed(1)}% respondem
              </p>
            </div>
            <TrendingUp className="h-10 w-10 text-indigo-500" />
          </div>
        </div>

        {/* Taxa de Engajamento */}
        <div 
          className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-6 border border-green-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => showDetail && showDetail('levantadas_com_mensagem')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Taxa de Engajamento do Lead</p>
              <p className="text-3xl font-bold text-green-700">{metrics.taxaEngajamento.toFixed(2)}%</p>
              <p className="text-sm text-green-600 mt-1">
                {metrics.levantadasComMensagem.toLocaleString()} de {metrics.totalLevantadasMao.toLocaleString()} levantadas com mensagem do lead
              </p>
            </div>
            <MessageSquare className="h-12 w-12 text-green-500" />
          </div>
        </div>

        {/* Taxa de Resposta */}
        <div 
          className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => showDetail && showDetail('levantadas_respondidas')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">Taxa de Resposta do Atendente</p>
              <p className="text-3xl font-bold text-purple-700">{metrics.taxaResposta.toFixed(2)}%</p>
              <p className="text-sm text-purple-600 mt-1">
                {metrics.levantadasRespondidas.toLocaleString()} de {metrics.levantadasComMensagem.toLocaleString()} com resposta do atendente
              </p>
            </div>
            <TrendingUp className="h-12 w-12 text-purple-500" />
          </div>
        </div>

        {/* Levantadas Não Respondidas */}
        <div 
          className="bg-gradient-to-r from-red-50 to-red-100 rounded-lg p-6 border border-red-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => showDetail && showDetail('levantadas_nao_respondidas')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-red-600">Não Respondidas pelo Atendente</p>
              <p className="text-3xl font-bold text-red-700">{(100 - metrics.taxaResposta).toFixed(2)}%</p>
              <p className="text-sm text-red-600 mt-1">
                {metrics.levantadasNaoRespondidas.toLocaleString()} de {metrics.levantadasComMensagem.toLocaleString()} levantadas com mensagem do lead
              </p>
            </div>
            <TrendingDown className="h-12 w-12 text-red-500" />
          </div>
        </div>

        {/* Tempo Médio de Resposta do Atendente */}
        <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-6 border border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-orange-600">Tempo Médio de Resposta do Atendente</p>
              <p className="text-3xl font-bold text-orange-700">{formatTempo(metrics.tempoMedioResposta)}</p>
              <p className="text-sm text-orange-600 mt-1">
                Para levantadas respondidas
              </p>
            </div>
            <Clock className="h-12 w-12 text-orange-500" />
          </div>
        </div>
        {/* Leads com múltiplas levantadas */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-6 border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => showDetail && showDetail('leads_multiplas')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Leads com múltiplas levantadas</p>
              <p className="text-3xl font-bold text-gray-700">{metrics.leadsMultiplasLevantadas.toLocaleString()}</p>
              <p className="text-sm text-gray-600 mt-1">
                Contagem de leads com mais de uma janela
              </p>
            </div>
            <Users className="h-12 w-12 text-gray-500" />
          </div>
        </div>
      </div>

      {/* Controle: Faixa de Tempo de Resposta do Atendente */}
      <div className="mt-6 bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h3 className="text-md font-semibold text-gray-800">Faixa de Tempo de Resposta do Atendente</h3>
            <p className="text-sm text-gray-500">Informe em minutos para ver contagem e percentual no período filtrado</p>
          </div>
          <div className="flex items-end gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Mín (min)</label>
              <input type="number" value={minTempo} onChange={(e) => setMinTempo(e.target.value)} className="w-24 p-2 border border-gray-300 rounded-md" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Máx (min)</label>
              <input type="number" value={maxTempo} onChange={(e) => setMaxTempo(e.target.value)} className="w-24 p-2 border border-gray-300 rounded-md" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">{faixaTempo.count.toLocaleString()}</div>
              <div className="text-xs text-gray-500">{faixaTempo.percent.toFixed(2)}% do total filtrado</div>
              <button
                onClick={() => showDetail && showDetail('tempo_resposta_custom', { min: Number(minTempo) || 0, max: Number(maxTempo) || 0 })}
                className="mt-2 text-sm text-blue-700 underline"
              >
                Ver registros
              </button>
            </div>
          </div>
        </div>
      </div>
      
    </div>
  );
};

export default LevantadasAnalysis;


